import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import './Login.css';
import './Policy.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function validatePassword(pw) {
  if (pw.length < 12) return 'Password must be at least 12 characters.';
  if (!/[A-Z]/.test(pw)) return 'Password must contain at least one uppercase letter.';
  if (!/[a-z]/.test(pw)) return 'Password must contain at least one lowercase letter.';
  if (!/\d/.test(pw)) return 'Password must contain at least one number.';
  if (!/[^A-Za-z\d]/.test(pw)) return 'Password must contain at least one special character.';
  return null;
}

function decodeJwtExpiry(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function InviteMessageCard({ children }) {
  return (
    <>
      <div className="centered-logo-container">
        <div className="logo-wrapper">
          <img src="/MP%20Full%20Color@3x.png" alt="Masters Path" className="site-logo-img" />
        </div>
      </div>
      <h2 className="login-title page-title">Set Up Your Account</h2>
      <div className="login-container">
        <div className="login-card" style={{ textAlign: 'center', padding: 32 }}>
          {children}
        </div>
      </div>
    </>
  );
}

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [tokenState, setTokenState] = useState(null); // 'expired' | 'used' | null

  useEffect(() => {
    document.documentElement.style.setProperty('--clouds-url', 'url(/clouds-dark.jpg)');
    document.body.classList.add('login-page');
    return () => {
      document.documentElement.style.removeProperty('--clouds-url');
      document.body.classList.remove('login-page');
    };
  }, []);

  // No token in URL
  if (!token) {
    return (
      <InviteMessageCard>
        <p style={{ color: '#ffb4b4', marginBottom: 16 }}>This invite link is missing or invalid.</p>
        <Link to="/login" style={{ color: '#ffb57a', fontSize: 14 }}>Back to login</Link>
      </InviteMessageCard>
    );
  }

  // Token present but already expired — check before showing the form
  const expiry = decodeJwtExpiry(token);
  if ((expiry && Date.now() > expiry) || tokenState === 'expired') {
    return (
      <InviteMessageCard>
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#ffb4b4" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 16 }}>
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        <p style={{ color: '#ffb4b4', fontWeight: 600, marginBottom: 8 }}>This invite link has expired.</p>
        <p style={{ color: 'rgba(220,235,255,0.5)', fontSize: 14, marginBottom: 24 }}>
          Invite links are valid for 72 hours. Please contact your administrator to send a new one.
        </p>
        <Link to="/login" style={{ color: '#ffb57a', fontSize: 14 }}>Back to login</Link>
      </InviteMessageCard>
    );
  }

  if (tokenState === 'used') {
    return (
      <InviteMessageCard>
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#9ff3c7" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 16 }}>
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
          <polyline points="22 4 12 14.01 9 11.01"/>
        </svg>
        <p style={{ color: '#9ff3c7', fontWeight: 600, marginBottom: 8 }}>This invite has already been redeemed.</p>
        <p style={{ color: 'rgba(220,235,255,0.5)', fontSize: 14, marginBottom: 24 }}>
          Your account is already set up. Use your credentials to log in.
        </p>
        <button className="login-button" onClick={() => navigate('/login')} style={{ width: '100%' }}>
          Go to Login
        </button>
      </InviteMessageCard>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const pwError = validatePassword(password);
    if (pwError) { setError(pwError); return; }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/accept-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = data.error || '';
        if (msg.toLowerCase().includes('already been used') || msg.toLowerCase().includes('already used')) {
          setTokenState('used');
          return;
        }
        if (msg.toLowerCase().includes('expired') || msg.toLowerCase().includes('invalid or expired')) {
          setTokenState('expired');
          return;
        }
        throw new Error(msg || 'Failed to activate account');
      }
      setDone(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="centered-logo-container">
        <div className="logo-wrapper">
          <img src="/MP%20Full%20Color@3x.png" alt="Masters Path" className="site-logo-img" />
        </div>
      </div>

      <h2 className="login-title page-title">Set Up Your Account</h2>

      <div className="login-container">
        <div className="login-card">
          {done ? (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#9ff3c7" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 16 }}>
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
              <p style={{ color: '#9ff3c7', fontWeight: 600, marginBottom: 8 }}>Account activated!</p>
              <p style={{ color: 'rgba(220,235,255,0.6)', fontSize: 14, marginBottom: 24 }}>
                Your password has been set. You can now log in.
              </p>
              <button className="login-button" onClick={() => navigate('/login')} style={{ width: '100%' }}>
                Go to Login
              </button>
            </div>
          ) : (
            <form className="login-form" onSubmit={handleSubmit}>
              <p style={{ color: 'rgba(220,235,255,0.6)', fontSize: 14, margin: '0 0 4px' }}>
                Choose a password to activate your account. Must be at least 12 characters with uppercase, lowercase, a number, and a special character.
              </p>

              <div className="input-with-icon" style={{ marginTop: 8 }}>
                <svg className="input-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <rect x="3" y="11" width="18" height="10" rx="2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7 11V8a5 5 0 0110 0v3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <input
                  className="login-input"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="New password"
                  aria-label="New password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={12}
                  autoFocus
                />
                <button
                  type="button"
                  className="input-icon-right input-toggle"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="-1 -1 26 26" fill="none" aria-hidden>
                      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                      <path d="M4 4L20 20" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="-1 -1 26 26" fill="none" aria-hidden>
                      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                    </svg>
                  )}
                </button>
              </div>

              <div className="input-with-icon">
                <svg className="input-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <rect x="3" y="11" width="18" height="10" rx="2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7 11V8a5 5 0 0110 0v3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <input
                  className="login-input"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Confirm password"
                  aria-label="Confirm password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>

              {error && <div className="login-error">{error}</div>}

              <button className="login-button" type="submit" disabled={loading}>
                {loading ? 'Activating…' : 'Activate Account'}
              </button>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
